// Utility to build a unique key from endpoint name + stable hash of args
export const buildKey = (endpointName: string, args: unknown) => {
  return `${endpointName}|${stableHash(args)}`;
};

/**
 * Generates a deterministic, human‑readable hash string for any JSON‑serializable value.
 *
 * This is not a cryptographic hash; it is intended for generating stable cache keys,
 * memoization identifiers, or offline request signatures where collisions are unlikely
 * but not security critical.
 *
 * @param value Any value to hash (primitives, arrays, plain objects).
 *
 * @returns A deterministic string representation suitable as a stable key.
 */
export const stableHash = (value: unknown): string => {
  if (value === null) return '_null_'; // null token
  if (value === undefined) return '_undefined_'; // undefined token

  // For primitives, add a type prefix
  const type = typeof value;
  if (type === 'string') return `str:${value}`;
  if (type === 'number') return `num:${value}`;
  if (type === 'boolean') return `bool:${value}`;

  if (Array.isArray(value)) {
    return '[' + value.map(stableHash).join(',') + ']';
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    return '{' + keys.map((k) => JSON.stringify(k) + ':' + stableHash(obj[k])).join(',') + '}';
  }

  // Fallback for other types (e.g., function, symbol)
  return String(value);
};

/**
 * True when the offline database feature is enabled via the REACT_APP_OFFLINE_DATABASE_ENABLED env var.
 */
export const isOfflineEnabled = process.env.REACT_APP_OFFLINE_DATABASE_ENABLED === 'true';

let counter = 0;

/**
 * Generates a temporary negative numeric id.
 *
 * Uses current milliseconds plus an incrementing counter to avoid same-ms collisions.
 */
export function generateDbRecordId(): number {
  return -(Date.now() * 1000 + counter++);
}

/**
 * Type guard: returns true if the id is a client temp id.
 */
export function isDbRecordId(id?: string | number): id is number {
  return isOfflineEnabled && typeof id === 'number' && id < 0;
}
